/**
 * Created by mac on 9/25/20
 */

const GitManager = function () {
    cleverapps.EventEmitter.call(this);

    this.folders = [];
    this.processing = 0;
    this.error = undefined;

    this.state = GitManager.STATE_READY;
    this.remote = {};
    this.local = {};
    this.cached = {};

    this.urls = {};

    cc.loader.getRes = cleverapps.extendFunc(cc.loader.getRes, function (url) {
        return cleverapps.git.urls[url && url.originalPath && url.originalPath()] || this._super(url);
    });

    addEventListener("beforeunload", this.handlePageUnload.bind(this));
};

GitManager.prototype = Object.create(cleverapps.EventEmitter.prototype);
GitManager.prototype.constructor = GitManager;

GitManager.prototype.updateStatus = function () {
    let status = GitManager.STATUS_EMPTY;
    const error = this.getError();

    if (this.checkAvailableErrors()) {
        status = GitManager.STATUS_UNAVAILABLE;
    } else if (this.isProcessing()) {
        status = GitManager.STATUS_PROCESSING;
    } else if (error === GitManager.PUSH || error === GitManager.REVERT) {
        status = GitManager.STATUS_ERROR;
    } else if (this.state === GitManager.STATE_DETACHED) {
        status = GitManager.STATUS_DETACHED;
    } else if (this.hasLocalChanges() || this.hasRemoteChanges()) {
        status = GitManager.STATUS_REDY;
    }

    if (status !== this.status) {
        this.status = status;

        clearTimeout(this.updateTimeout);
        this.updateTimeout = setTimeout(this.trigger.bind(this, "changed"), 0);
    }
};

GitManager.prototype.getStatus = function () {
    return this.status;
};

GitManager.prototype.setState = function (state, changes) {
    this.state = state || GitManager.STATE_NONE;
    this.remote = changes || {};
};

GitManager.prototype.checkAvailableErrors = function () {
    if (cleverapps.isLocalhost()) {
        return undefined;
    }

    if (!this.getUserName()) {
        return "Login before you can process changes.";
    }

    return undefined;
};

GitManager.prototype.getUserName = function () {
    return AdminAccess.userName;
};

GitManager.prototype.getError = function () {
    return this.error;
};

GitManager.prototype.isProcessing = function () {
    return this.processing > 0;
};

GitManager.prototype.changeFolders = function (folders) {
    this.folders = cleverapps.toArray(folders) || [];
    this.update();
};

GitManager.prototype.update = function () {
    const error = this.checkAvailableErrors();
    if (error) {
        return;
    }

    this.request(GitManager.STATUS, {});
};

GitManager.prototype.validate = function (files) {
    files = cleverapps.toArray(files).map((file) => {
        file = file.split(":");
        return file[1] || file[0];
    });

    let errors = [];

    for (let i = 0; i < files.length; i++) {
        const changes = this.local[files[i]];
        const editor = GitManager.EDITORS[changes.type];
        const content = this.getContent(files[i]);

        const fileErrors = editor.getValidateError && editor.getValidateError(content);
        if (fileErrors) {
            errors = errors.concat(fileErrors);
        }
    }

    if (errors.length > 0) {
        cleverapps.git.displayErrorWindow(errors.join("\n"));
    }

    return errors.length === 0;
};

GitManager.prototype.push = function (comment, files, callback) {
    if (!this.validate(files)) {
        return;
    }

    callback = cleverapps.once(callback);
    files = cleverapps.toArray(files).map((file) => {
        file = file.split(":");
        return file[1] || file[0];
    });

    const options = {
        timeout: 30000
    };

    const pending = {};

    files.forEach((file) => {
        if (this.local[file]) {
            pending[file] = this.local[file].content;
        }

        if (this.remote[file]) {
            pending[file] = true;
        }
    });

    const username = this.getUserName();
    const data = {
        comment: `user: ${username}\ncomment: "${comment}"`,
        files: pending
    };

    const onComplete = function (error) {
        if (error) {
            callback(error);
            return;
        }

        for (const file in pending) {
            if (this.local[file] && this.local[file].content === pending[file]) {
                delete this.local[file];
            }
        }

        this.save();
        this.updateStatus();

        cleverapps.administrator && cleverapps.administrator.log.createEntry({
            type: AdminLog.GIT_PUSH,
            data: Object.keys(pending).join(" ")
        });

        callback();
    }.bind(this);

    this.request(GitManager.PUSH, data, onComplete, options);
};

GitManager.prototype.autoPush = function (file) {
    if (cleverapps.isLocalhost() || this.checkAvailableErrors() || this.isProcessing()) {
        return;
    }

    const files = this.listLocalChanges().filter((change) => !file || change === file);
    if (!files.length) {
        return;
    }

    this.push(this.getAutoPushComment(files), files);
};

GitManager.prototype.getAutoPushComment = function (files) {
    const changes = this.local[files[0]];
    const version = changes && changes.content && changes.content.version;

    return String(version || "").split(" ")[0] || "autopush";
};

GitManager.prototype.revert = function (files, callback) {
    files = cleverapps.toArray(files);

    const local = [];
    const remote = [];

    files.forEach((path) => {
        path = path.split(":");

        const file = path[1] || path[0];

        if (path[0] === "local") {
            local.push(file);
        } else {
            remote.push(file);
        }
    });

    this.revertLocalFiles(local);
    this.revertRemoteFiles(remote, callback);
};

GitManager.prototype.revertLocalFiles = function (files) {
    this.restore(files);
    this.save();
    this.updateStatus();
};

GitManager.prototype.revertRemoteFiles = function (files, callback) {
    callback = cleverapps.once(callback);

    if (!files.length) {
        callback();
        return;
    }

    const data = {
        files
    };

    const onComplete = function (error) {
        if (error) {
            callback(error);
            return;
        }

        cleverapps.administrator && cleverapps.administrator.log.createEntry({
            type: AdminLog.GIT_REVERT,
            data: data.files.join(" ")
        });

        callback();
    };

    this.request(GitManager.REVERT, data, onComplete);
};

GitManager.prototype.request = function (command, data, callback, options) {
    options = { ignoreNoRest: true, ...options || {} };
    callback = cleverapps.once(callback);

    const onError = function (error) {
        error = `git error: ${error}`;

        console.error(error);
        cleverapps.notification.create(error);

        callback(error);
    };

    const error = this.checkAvailableErrors();
    if (error) {
        onError(error);
        return;
    }

    if (this.isProcessing() && GitManager.STATUS !== command) {
        onError("Previous request still in process. Try again later.");
        return;
    }

    this.processing += 1;
    this.updateStatus();

    const onSuccess = function (response) {
        this.processing -= 1;

        const error = response && response.error;
        if (error) {
            this.error = command;
            this.updateStatus();
            onError(error);
        } else {
            this.error = undefined;
            this.setState(response.state, response.changes);
            this.updateStatus();
            callback();
        }
    }.bind(this);

    // A dead rest server used to be swallowed here on localhost, which made every edit look saved:
    // the change went to the local cache, nothing ever reached the repo, and the level came back
    // still tagged after a reload with nothing on screen to say why. Localhost is where the editor
    // backend is most often not running, so it is the last place to hide this. Identical messages
    // collapse in the notification queue, so a dead backend costs one toast, not one per request.
    const onFailure = function (request) {
        this.processing -= 1;
        this.error = command;
        this.updateStatus();

        onError(`Request failed for '${command}' git command with error ${request.status} ${request.statusText}`);
    }.bind(this);

    cleverapps.RestClient.post(`/gitcommands/${command}`, data, onSuccess, onFailure, options);
};

GitManager.prototype.getLastCommit = function (data, onSuccess, onError) {
    cleverapps.RestClient.post("/gitcommands/lastcommit", data, onSuccess, onError);
};

GitManager.prototype.hasRemoteChanges = function () {
    return this.listRemoteChanges().length > 0;
};

GitManager.prototype.listRemoteChanges = function (folders) {
    folders = cleverapps.toArray(folders);

    return Object.keys(this.remote).filter((file) => !folders || GitManager.match(file, folders));
};

GitManager.prototype.hasLocalChanges = function () {
    return this.listLocalChanges().length > 0;
};

GitManager.prototype.listLocalChanges = function (folders) {
    folders = cleverapps.toArray(folders);

    return Object.keys(this.local).filter((file) => {
        if (folders && !GitManager.match(file, folders)) {
            return false;
        }

        const stringify = (obj) => JSON.stringify(obj, (key, value) => (key === "commandUid" ? undefined : value));

        const localData = this.local[file];
        const cachedData = this.cached[file];
        if (localData && cachedData && stringify(localData.content) === stringify(cachedData.content)) {
            return false;
        }

        return true;
    });
};

GitManager.prototype.listAllChanges = function () {
    const files = [];

    this.listLocalChanges().forEach((file) => {
        files.push(`local:${file}`);
    });

    this.listRemoteChanges().forEach((file) => {
        files.push(`remote:${file}`);
    });

    return files;
};

GitManager.prototype.handleSceneClose = function () {
    if (!cleverapps.isLocalhost()) {
        if (this.hasLocalChanges()) {
            if (!window.confirm("There are some unsaved changes, are you sure you want to exit? All unsaved changes will be lost.")) {
                this.displayWindow();
                return false;
            }
        }

        if (this.hasRemoteChanges()) {
            if (!window.confirm("Some changes on server wasn't pushed properly, are you sure you want to exit?")) {
                this.displayWindow();
                return false;
            }
        }

        if (this.isProcessing()) {
            if (!window.confirm("Some requests still in process, are you sure you want to exit? All unsaved changes will be lost.")) {
                return false;
            }
        }
    }

    cleverapps.git.changeFolders(undefined);

    return true;
};

GitManager.prototype.handlePageUnload = function (event) {
    if (cleverapps.isLocalhost()) {
        return true;
    }

    this.deprecateLocalChanges();

    if (this.hasLocalChanges() || this.hasRemoteChanges() || this.isProcessing()) {
        this.displayWindow();
        event.preventDefault();
        event.returnValue = "Please save changes. All unsaved changes will be lost.";
        return false;
    }

    return true;
};

GitManager.prototype.load = function () {
    const data = cleverapps.dataLoader.load(DataLoader.TYPES.GIT) || {};
    const local = { ...(data.local || {}) };

    for (const file in local) {
        const changes = local[file];
        if (changes.deprecated || (changes.date < Date.now() - GitManager.CHANGES_LIFETIME)) {
            delete local[file];
        }
    }

    const names = cleverapps.unique(Object.keys(local)
        .map((file) => {
            const changes = local[file];
            const editor = GitManager.EDITORS[changes.type];

            return editor.getBundleNames ? editor.getBundleNames(changes) : [];
        }).flat().map((bundleName) => {
            const bundle = bundles[bundleName];
            return bundle && bundle.virtual && bundle.injectTo ? bundle.injectTo[0] : bundleName;
        })
        .filter((bundleName) => bundleName && bundles[bundleName]));

    const onSuccess = () => {
        this.local = local;

        for (const file in this.local) {
            const changes = this.local[file];
            const editor = GitManager.EDITORS[changes.type];

            this.cache(changes.type, changes);

            editor.load(file, changes);
        }

        this.updateStatus();

        cleverapps.bundleLoader.deleteBundles(names);
    };

    cleverapps.bundleLoader.loadBundles(names, { onSuccess });
};

GitManager.prototype.save = function () {
    const data = {
        local: this.local
    };

    cleverapps.dataLoader.save(DataLoader.TYPES.GIT, data);
};

GitManager.prototype.edit = function (type, data, callback) {
    const editor = GitManager.EDITORS[type];

    this.cache(type, data);

    const changes = editor.apply(data);

    for (const file in changes) {
        changes[file].type = type;
        changes[file].date = Date.now();
        this.local[file] = changes[file];
    }

    this.save();
    this.updateStatus();

    cleverapps.dataLoader.processSaveQueue();

    callback && callback();
};

GitManager.prototype.remove = function (type, data) {
    data.content = null;
    this.edit(type, data);
};

GitManager.prototype.cache = function (type, data) {
    const editor = GitManager.EDITORS[type];
    const cached = editor.cache(data);

    for (const file in cached) {
        if (!this.cached[file]) {
            cached[file].type = type;
            this.cached[file] = cached[file];
        }
    }
};

GitManager.prototype.restore = function (files) {
    for (let i = 0; i < files.length; ++i) {
        const file = files[i];

        const cached = this.cached[file];
        const editor = GitManager.EDITORS[cached.type];

        editor.restore(file, cached);

        delete this.cached[file];
        delete this.local[file];
    }
};

GitManager.prototype.displayWindow = function (action) {
    cleverapps.focusManager.display({
        stack: true,
        focus: "GitInfoWindow",
        action: function (f) {
            new GitInfoWindow(action);
            cleverapps.focusManager.onceNoWindowsListener = f;
        }
    });
};

GitManager.prototype.displayErrorWindow = function (debugInfo) {
    cleverapps.focusManager.display({
        stack: true,
        focus: "ErrorWindow",
        action: function (f) {
            new ErrorWindow({
                debugInfo
            });
            cleverapps.focusManager.onceNoWindowsListener = f;
        }
    });
};

GitManager.prototype.getContent = function (file) {
    file = file.split(":");
    file = file[1] || file[0];

    const changes = this.local[file];
    return changes && changes.content;
};

GitManager.prototype.deprecateLocalChanges = function () {
    let needSave = false;

    for (const file in this.local) {
        this.local[file].deprecated = true;
        needSave = true;
    }

    if (needSave) {
        this.save();
        cleverapps.dataLoader.processSaveQueue();
    }
};

GitManager.CHANGES_LIFETIME = cleverapps.parseInterval("1 day");
